import { useNavigate } from "react-router-dom"
import { useIntl, FormattedMessage } from "react-intl"
import classNames from "classnames"
import { useForm, useFieldArray } from "react-hook-form"
import { useStateMachine } from "little-state-machine"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import Layout from "modules/claims/components/Layout"
import ProgressBar from "modules/claims/components/StepForm/ProgressBar"
import InputText from "components/InputMUI/Text"
import { getCurrentLanguageAndInstanceCode } from "services/instances"
import { updateAction } from "utils/littleStateMachine"
import { GENERIC_MSGS, CUSTOMER_NUMBER_TOOLTIP } from "modules/claims/constants"
import { VALIDATION_MESSAGES } from "utils/forms"
import { BUTTON_MESSAGES } from "utils/buttons"
import { MSGS, COUNTRIES_OPTIONS } from "./constants"
import "../styles/step-form.scss"
import contactDetails from "./img/1.contact-details.jpg"
import Select from "components/InputMUI/Select"

const selectPlaceholder = GENERIC_MSGS.choose_placeholder
const inputPlaceholder = GENERIC_MSGS.typeAnswer_placeholder

const getInitialEmails = (email) => {
  if (Array.isArray(email) && email.length) return email
  if (email) return [email]
  return [""]
}

const Step1 = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { actions, state } = useStateMachine({ updateAction })
  const [lang, instance] = getCurrentLanguageAndInstanceCode()
  const initialEmails = getInitialEmails(state.email)

  const defaultValues = {
    ...state,
    email: initialEmails.map((value) => ({ value }))
  }
  const schema = yup.object({
    companyName: yup
      .string()
      .matches(
        /^[\p{L}\p{N} .,&'-]+$/u,
        intl.formatMessage(MSGS.companyNameInvalidCharacters)
      )
      .required(intl.formatMessage(VALIDATION_MESSAGES.required)),
    companyCountry: yup.string().required(intl.formatMessage(VALIDATION_MESSAGES.required)),
    customerNumber: yup.string().required(intl.formatMessage(VALIDATION_MESSAGES.required)).min(5, intl.formatMessage(MSGS.minLength, { min: 5 })).max(5, intl.formatMessage(MSGS.maxLength, { max: 5 })),
    fullName: yup.string().required(intl.formatMessage(VALIDATION_MESSAGES.required)),
    lastname: yup.string().required(intl.formatMessage(VALIDATION_MESSAGES.required)),
    email: yup
      .array()
      .of(
        yup.object({
          value: yup.string().trim().email()
        })
      )
      .test(
        "at-least-one-email",
        intl.formatMessage(VALIDATION_MESSAGES.required),
        (emails) =>
          Array.isArray(emails) && emails.some((item) => item?.value && item.value.trim())
      )
  })

  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues,
    // mode: "onChange",
    resolver: yupResolver(schema)
  })

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail
  } = useFieldArray({ control, name: "email" })

  const onSubmit = (data) => {
    actions.updateAction({
      ...data,
      companyName: data.companyName.toUpperCase(),
      email: data.email.map(({ value }) => value.trim()).filter(Boolean)
    })
    navigate(`../${lang}-${instance}/claims/step-2`, { replace: true })
  }

  const handleAddEmail = () => {
    appendEmail({ value: "" })
  }

  const handleRemoveEmail = (index) => {
    if (emailFields.length > 1) removeEmail(index)
  }

  return (
    <Layout
      title={
        <FormattedMessage
          id="Claims.ContactDetail.title"
          description="section claim contact detail title"
          defaultMessage="Contact Details"
        />
      }
      backgroundImage={contactDetails}
    >
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputText
            name="companyName"
            register={register}
            label={MSGS.companyName}
            placeholder={inputPlaceholder}
            defaultValue={state.companyName}
            error={errors.companyName?.message}
          />
          <Select
            name="companyCountry"
            control={control}
            label={MSGS.companyCountry}
            placeholder={selectPlaceholder}
            options={COUNTRIES_OPTIONS}
            defaultValue={state.companyCountry}
            error={errors?.companyCountry?.message}
          />
          <InputText
            name="customerNumber"
            register={register}
            label={MSGS.customerNumber}
            placeholder={inputPlaceholder}
            defaultValue={state.customerNumber}
            tooltipElem={CUSTOMER_NUMBER_TOOLTIP}
            error={errors.customerNumber?.message}
          />
          <InputText
            name="fullName"
            register={register}
            label={MSGS.fullName}
            placeholder={inputPlaceholder}
            defaultValue={state.fullName}
            error={errors.fullName?.message}
          />
          <InputText
            name="lastname"
            register={register}
            label={MSGS.lastname}
            placeholder={inputPlaceholder}
            defaultValue={state.lastname}
            error={errors.lastname?.message}
          />
          {emailFields.map((field, index) => (
            <div className="step-form--email-row" key={field.id}>
              <InputText
                name={`email.${index}.value`}
                register={register}
                label={MSGS.email}
                placeholder={inputPlaceholder}
                defaultValue={field.value}
                error={
                  errors.email?.[index]?.value?.message ||
                  (index === 0 ? errors.email?.message : undefined)
                }
                type="email"
              />
              {emailFields.length > 1 && (
                <button
                  aria-label="Remove email"
                  className="btn btn-white step-form--email-remove border border-black rounded-4 mt-2"
                  onClick={() => handleRemoveEmail(index)}
                  type="button"
                >
                  −
                </button>
              )}
              {index === emailFields.length - 1 && (
                <button
                  aria-label="Add email"
                  className="btn btn-white step-form--email-add border border-black rounded-4 mt-2"
                  onClick={handleAddEmail}
                  type="button"
                >
                  +
                </button>
              )}
            </div>
          ))}
          <div className="btn-container">
            <button
              className={classNames({
                "btn btn-dark text-uppercase": true,
                disabled: Object.values(errors).length
              })}
              type="submit"
            >
              {intl.formatMessage(BUTTON_MESSAGES.ok)}
            </button>
          </div>
        </form>
        <ProgressBar progress={10} handleNext={handleSubmit(onSubmit)} RoutePrev={`../${lang}-${instance}`}/>
      </>
    </Layout>
  )
}

export default Step1
